import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, AlertTriangle, Thermometer, Activity, Trash2, Info } from "lucide-react";
import { formatNum, formatSgd } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { ScenarioDetail } from "../components/ScenarioDetail";
import { toast } from "sonner";

const TYPES = ["Physical", "Transition"];
const SUBTYPES = {
  Physical: ["Acute", "Chronic"],
  Transition: ["Policy & Legal", "Technology", "Market", "Reputation"],
};
const HORIZONS = ["Short-term (2026)", "Medium-term (2030)", "Long-term (2050)"];
const LIKELIHOOD = ["Low", "Medium", "High", "Certain"];
const IMPACT = ["Low", "Medium", "High"];

export default function Scenarios() {
  const [catalogue, setCatalogue] = useState([]);
  const [selected, setSelected] = useState([]);
  const [risks, setRisks] = useState([]);
  const [riskOpen, setRiskOpen] = useState(false);
  const [lane, setLane] = useState("All");
  const [detail, setDetail] = useState(null);
  const [riskForm, setRiskForm] = useState({
    title: "", type: "Physical", subtype: "Acute", horizon: "Medium-term (2030)",
    likelihood: "Medium", impact: "Medium", score: 4, financial_impact_sgd: "",
    scenario_code: "", mitigation: "", status: "monitoring",
  });

  const load = async () => {
    const [c, s, r] = await Promise.all([
      api.get("/scenarios/catalogue"),
      api.get("/scenarios/selected"),
      api.get("/scenarios/risks"),
    ]);
    setCatalogue(c.data.scenarios);
    setSelected(s.data.selections);
    setRisks(r.data.risks);
  };
  useEffect(() => { load(); }, []);

  const toggleScenario = async (code) => {
    const isSelected = selected.some((s) => s.scenario_code === code);
    await api.post("/scenarios/select", { scenario_code: code, selected: !isSelected });
    load();
  };

  const createRisk = async () => {
    try {
      const payload = {
        ...riskForm,
        score: parseInt(riskForm.score),
        financial_impact_sgd: riskForm.financial_impact_sgd ? parseFloat(riskForm.financial_impact_sgd) : null,
        scenario_code: riskForm.scenario_code || null,
      };
      await api.post("/scenarios/risks", payload);
      toast.success("Risk added to register");
      setRiskOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeRisk = async (id) => {
    if (!window.confirm("Delete this risk?")) return;
    await api.delete(`/scenarios/risks/${id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <div className="label-eyebrow mb-1">Module · TCFD / IFRS S2 · NGFS</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">Climate Scenarios & Risk <ProvenanceTrigger pkey="scenarios.impact" className="!opacity-60 hover:!opacity-100" /></h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
          NGFS / IEA reference scenarios with physical &amp; transition risk register, aligned with TCFD pillars embedded in IFRS S2.
        </p>
      </div>

      {/* Physical / Transition lane filter */}
      <div className="flex gap-2 flex-wrap" data-testid="lane-filter">
        {["All", "Physical", "Transition"].map((l) => {
          // F-072: badge counts must match the risk-register cards shown beneath (not catalogue + risks).
          const n = l === "All" ? risks.length : risks.filter((r) => r.type === l).length;
          return (
            <button key={l} onClick={() => setLane(l)} data-testid={`lane-${l.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-sm border text-xs font-mono transition-colors ${lane === l ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:bg-surface-hover"}`}>
              {l} <span className="opacity-70">({n})</span>
            </button>
          );
        })}
      </div>

      {/* Scenario catalogue */}
      <div>
        <div className="label-eyebrow mb-3">Reference scenarios — select what you use · click Details for sources &amp; what-if</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {catalogue.filter((s) => lane === "All" || s.category === lane).map((s) => {
            const isSel = selected.some((x) => x.scenario_code === s.code);
            return (
              <div key={s.code} role="button" tabIndex={0} onClick={() => toggleScenario(s.code)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleScenario(s.code); } }}
                className={`text-left card-flat p-5 transition-colors cursor-pointer ${isSel ? "border-accent bg-accent/5" : "hover:bg-surface-hover"}`}
                data-testid={`scenario-${s.code}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {s.category === "Physical" ? <Thermometer className="h-4 w-4 text-red-400" /> : <Activity className="h-4 w-4 text-accent" />}
                    <Badge variant={s.category === "Physical" ? "danger" : "lime"}>{s.category}</Badge>
                    <Badge variant="outline">{s.warming}</Badge>
                  </div>
                  {isSel && <Badge variant="success">selected</Badge>}
                </div>
                <h3 className="font-display font-bold text-lg mb-1">{s.name}</h3>
                <div className="font-mono text-xs text-muted-foreground mb-3 flex items-center justify-between">
                  <span>{s.code}</span>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px]" data-testid={`scenario-detail-${s.code}`}
                    onClick={(e) => { e.stopPropagation(); setDetail({ kind: "scenario", ref: s.code, title: s.name }); }}>
                    <Info className="h-3 w-3" /> Details &amp; what-if
                  </Button>
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">{s.narrative}</p>
                <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-3 text-xs font-mono">
                  <div>
                    <div className="text-muted-foreground">2030 carbon price</div>
                    <div className="font-bold text-base">{formatSgd(s.carbon_price_2030_sgd)}/t</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">2050 carbon price</div>
                    <div className="font-bold text-base">{formatSgd(s.carbon_price_2050_sgd)}/t</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Risk register */}
      <div>
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="label-eyebrow">Climate Risk Register</div>
            <h2 className="font-display font-semibold text-xl" data-testid="register-count-heading">{(lane === "All" ? risks : risks.filter((r) => r.type === lane)).length} risks tracked{lane !== "All" ? ` · ${lane}` : ""}</h2>
          </div>
          <Dialog open={riskOpen} onOpenChange={setRiskOpen}>
            <DialogTrigger asChild>
              <Button variant="primary" size="sm" data-testid="add-risk-btn"><Plus className="h-3.5 w-3.5" /> Add risk</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New climate risk</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Title</Label><Input value={riskForm.title} onChange={(e) => setRiskForm({...riskForm, title: e.target.value})} data-testid="risk-title" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={riskForm.type} onValueChange={(v) => setRiskForm({...riskForm, type: v, subtype: SUBTYPES[v][0]})}>
                    <SelectTrigger data-testid="risk-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subtype</Label>
                  <Select value={riskForm.subtype} onValueChange={(v) => setRiskForm({...riskForm, subtype: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{SUBTYPES[riskForm.type].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Horizon</Label>
                  <Select value={riskForm.horizon} onValueChange={(v) => setRiskForm({...riskForm, horizon: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{HORIZONS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Aligned scenario</Label>
                  <Select value={riskForm.scenario_code || "none"} onValueChange={(v) => setRiskForm({...riskForm, scenario_code: v === "none" ? "" : v})}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {catalogue.map(s => <SelectItem key={s.code} value={s.code}>{s.code} · {s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Likelihood</Label>
                  <Select value={riskForm.likelihood} onValueChange={(v) => setRiskForm({...riskForm, likelihood: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{LIKELIHOOD.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Impact</Label>
                  <Select value={riskForm.impact} onValueChange={(v) => setRiskForm({...riskForm, impact: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{IMPACT.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Score 1-10</Label><Input type="number" min={1} max={10} value={riskForm.score} onChange={(e) => setRiskForm({...riskForm, score: e.target.value})} data-testid="risk-score" /></div>
                <div><Label>Financial impact (SGD)</Label><Input type="number" value={riskForm.financial_impact_sgd} onChange={(e) => setRiskForm({...riskForm, financial_impact_sgd: e.target.value})} placeholder="Optional" /></div>
                <div className="col-span-2"><Label>Mitigation</Label><Textarea value={riskForm.mitigation} onChange={(e) => setRiskForm({...riskForm, mitigation: e.target.value})} rows={3} data-testid="risk-mitigation" /></div>
              </div>
              <Button variant="primary" onClick={createRisk} disabled={!riskForm.title} data-testid="risk-submit-btn">Save risk</Button>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {risks.filter((r) => lane === "All" || r.type === lane).map((r) => (
            <div key={r.id} className="card-flat p-5" data-testid="risk-card">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Badge variant={r.type === "Physical" ? "danger" : "warning"}>{r.type}</Badge>
                  <Badge variant="outline">{r.subtype}</Badge>
                  <Badge variant="outline">{r.horizon}</Badge>
                </div>
                <div className="flex items-center">
                  <Button variant="ghost" size="icon" onClick={() => setDetail({ kind: "risk", ref: r.id, title: r.title })} data-testid={`risk-detail-${r.id}`} title="Details, sources & what-if"><Info className="h-3.5 w-3.5 text-accent" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => removeRisk(r.id)} data-testid="delete-risk-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                </div>
              </div>
              <button className="font-display font-semibold text-lg leading-tight mb-3 text-left hover:text-accent transition-colors"
                onClick={() => setDetail({ kind: "risk", ref: r.id, title: r.title })} data-testid={`risk-title-${r.id}`}>{r.title}</button>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <Cell label="Likelihood" v={r.likelihood} />
                <Cell label="Impact" v={r.impact} />
                <Cell label="Score" v={`${r.score}/10`} hi={r.score >= 7} />
              </div>
              {r.financial_impact_sgd && (
                <div className="mb-3">
                  <div className="label-eyebrow mb-1">Financial impact (est.)</div>
                  <div className="font-mono font-bold text-lg">{formatSgd(r.financial_impact_sgd)}</div>
                </div>
              )}
              {r.scenario_code && <Badge variant="lime" className="mb-2">Aligned: {r.scenario_code}</Badge>}
              {r.mitigation && (
                <div className="text-xs text-muted-foreground border-l-2 border-accent pl-3 mt-3 italic">{r.mitigation}</div>
              )}
            </div>
          ))}
          {risks.length === 0 && <div className="md:col-span-2 card-flat p-12 text-center text-muted-foreground">No risks yet. Click <span className="text-accent">Add risk</span> to start the register.</div>}
        </div>
      </div>
      {detail && <ScenarioDetail kind={detail.kind} refId={detail.ref} title={detail.title} onClose={() => setDetail(null)} />}
    </div>
  );
}

function Cell({ label, v, hi }) {
  return (
    <div className="card-flat p-2 bg-surface">
      <div className="label-eyebrow text-[9px] mb-0.5">{label}</div>
      <div className={`font-mono text-sm font-bold ${hi ? "text-red-400" : ""}`}>{v}</div>
    </div>
  );
}
